import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { ResponseLogViewDto } from 'services/api/requests/statusLogs/statusLogs';
import { useLogWebSocket } from 'services/websocket/useLogWebSocket';

interface LogWebSocketContextType {
	hasNewLogs: boolean;
	clearNewLogs: () => void;
	isConnected: boolean;
	setModalOpen: (isOpen: boolean) => void;
}

const LogWebSocketContext = createContext<LogWebSocketContextType>({
	hasNewLogs: false,
	clearNewLogs: () => {},
	isConnected: false,
	setModalOpen: () => {},
});

export const useLogWebSocketContext = () => useContext(LogWebSocketContext);

interface LogWebSocketProviderProps {
	children: ReactNode;
}

export const LogWebSocketProvider = ({
	children,
}: LogWebSocketProviderProps) => {
	const [hasNewLogs, setHasNewLogs] = useState(false);
	const queryClient = useQueryClient();
	const toast = useToast();
	const connectionTimestampRef = useRef<number>(0);
	const lastLogTimestampRef = useRef<number>(0);
	const isReadyToProcessLogsRef = useRef<boolean>(false);
	const isModalOpenRef = useRef<boolean>(false);
	const processedToastLogsRef = useRef<Set<number>>(new Set());

	const iconRefreshInvalidationQuery = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ['log-list'],
			exact: false,
		});
	}, [queryClient]);

	const handleNewLog = useCallback(
		(logData: ResponseLogViewDto) => {
			if (!isReadyToProcessLogsRef.current) {
				return;
			}

			const now = Date.now();
			const logTimestamp = logData.data_hora
				? new Date(logData.data_hora).getTime()
				: now;

			const connectionTimeWithMargin =
				connectionTimestampRef.current + 2000;
			const wasCreatedAfterConnection =
				logTimestamp >= connectionTimeWithMargin;

			const hasAsyncJob = !!logData.async_job;
			const isNewLog = logTimestamp > lastLogTimestampRef.current;

			const timeDiff = now - logTimestamp;
			const isRecentLog =
				hasAsyncJob || (timeDiff >= 0 && timeDiff < 30000);

			if (
				(!wasCreatedAfterConnection && !hasAsyncJob) ||
				(!isNewLog && !hasAsyncJob) ||
				!isRecentLog
			) {
				return;
			}

			if (isNewLog) {
				lastLogTimestampRef.current = logTimestamp;
			}

			const isAsyncJob = !!logData.async_job;
			const isBulkCreateOrImportExport =
				logData.acao === 'BULKCREATE' ||
				logData.acao === 'IMPORT' ||
				logData.acao === 'EXPORT';
			const isPostMethod = logData.metodo === 'POST' || !logData.metodo;
			const isImportOrExport =
				isAsyncJob || (isBulkCreateOrImportExport && isPostMethod);

			if (logData.async_job || isImportOrExport) {
				iconRefreshInvalidationQuery();
			}

			if (isImportOrExport) {
				setHasNewLogs(true);
			}

			if (logData.async_job) {
				const {
					status,
					process_type_label,
					download_url,
					result_description,
				} = logData.async_job;

				if (
					status === 'completed' &&
					download_url &&
					!isModalOpenRef.current &&
					logData.id &&
					!processedToastLogsRef.current.has(logData.id)
				) {
					processedToastLogsRef.current.add(logData.id);
					toast({
						title: 'Sucesso!',
						description:
							result_description ||
							`${
								process_type_label || 'Processo'
							} finalizado com sucesso!`,
						position: 'bottom-left',
						status: 'success',
						duration: 8000,
						isClosable: true,
					});
				}

				if (
					status === 'failed' &&
					!isModalOpenRef.current &&
					logData.id &&
					!processedToastLogsRef.current.has(logData.id)
				) {
					processedToastLogsRef.current.add(logData.id);
					toast({
						title: 'Erro',
						description:
							result_description ||
							`${
								process_type_label || 'Processo'
							} falhou. Tente novamente.`,
						position: 'bottom-left',
						status: 'error',
						duration: 8000,
						isClosable: true,
					});
				}
			}
		},
		[iconRefreshInvalidationQuery, toast]
	);

	const { isConnected } = useLogWebSocket({
		onNewLog: handleNewLog,
		enabled: true,
	});

	useEffect(() => {
		if (isConnected) {
			const now = Date.now();
			connectionTimestampRef.current = now;
			lastLogTimestampRef.current = now;
			setHasNewLogs(false);
			isReadyToProcessLogsRef.current = false;

			const gracePeriodTimeout = setTimeout(() => {
				isReadyToProcessLogsRef.current = true;
			}, 3000);

			return () => {
				clearTimeout(gracePeriodTimeout);
			};
		} else {
			isReadyToProcessLogsRef.current = false;
		}
	}, [isConnected]);

	const clearNewLogs = useCallback(() => {
		setHasNewLogs(false);
	}, []);

	const setModalOpen = useCallback((isOpen: boolean) => {
		isModalOpenRef.current = isOpen;
	}, []);

	const contextValue = useMemo(
		() => ({
			hasNewLogs,
			clearNewLogs,
			isConnected,
			setModalOpen,
		}),
		[hasNewLogs, clearNewLogs, isConnected, setModalOpen]
	);

	return (
		<LogWebSocketContext.Provider value={contextValue}>
			{children}
		</LogWebSocketContext.Provider>
	);
};
